import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp } from "@/lib/motion";

// Category slugs that render a product video instead of a static image.
// `position` tunes object-position so the product stays in the clear zone
// above the bottom text overlay.
const CATEGORY_VIDEOS = {
  exterior: { src: "/exterior-care.mp4", position: "center 35%" },
  interior: { src: "/interior-care.mp4", position: "center 35%" },
  accessories: { src: "/detailing-accessories.mp4", position: "center 30%" },
  aerosols: { src: "/aerosols.mp4", position: "center 35%" },
  polishes: { src: "/polishes-wax.mp4", position: "center 35%" },
  kits: { src: "/car-care-kits.mp4", position: "center 65%" },
};

export const CategoryCard = ({ category, large = false }) => {
  const name = (category.name || "").toLowerCase();
  const video =
    CATEGORY_VIDEOS[category.slug] ||
    (name.includes("exterior")
      ? CATEGORY_VIDEOS.exterior
      : name.includes("interior")
      ? CATEGORY_VIDEOS.interior
      : name.includes("detailing") || name.includes("accessor")
      ? CATEGORY_VIDEOS.accessories
      : null);
  const useVideo = Boolean(video);

  return (
    <motion.div variants={fadeUp} className={large ? "sm:row-span-2" : ""}>
      <Link
        to={`/shop?category=${category.slug}`}
        data-testid="category-card"
        className={`group relative block h-full overflow-hidden rounded-[var(--acx-radius-md)] border border-[color:var(--acx-border)] bg-[var(--acx-card)] ${
          large ? "min-h-[320px] sm:min-h-[460px]" : "min-h-[220px]"
        }`}
      >
        {useVideo ? (
          <video
            src={video.src}
            poster={category.image}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            data-testid="category-card-video"
            className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-105"
            style={{ objectPosition: video.position }}
          />
        ) : (
          <img
            src={category.image}
            alt={category.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-75"
          />
        )}
        <div
          className={`absolute inset-0 ${
            useVideo
              ? "bg-gradient-to-t from-black via-black/45 to-black/10"
              : "bg-gradient-to-t from-black via-black/40 to-transparent"
          }`}
        />
        <div className="relative flex h-full flex-col justify-end p-6">
          <span className="mb-2 text-[11px] uppercase tracking-[0.2em] text-white/55">
            {category.productCount} products
          </span>
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">
              {category.name}
            </h3>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-all duration-300 group-hover:bg-[#E11B22] group-hover:text-white">
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
          <p className="mt-2 max-w-xs text-[13px] leading-relaxed text-white/60">{category.tagline}</p>
        </div>
      </Link>
    </motion.div>
  );
};

export default CategoryCard;
